import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./styles/app.css";
import { loadConfig } from "./config";
import { initAppInsights, trackError } from "./telemetry/appInsights";
import { eventBus } from "./services/eventBus";
import { useUiStore } from "./store/uiStore";
import { AuthProvider } from "./auth/AuthProvider";
import { App } from "./App";

/**
 * Bootstrap order: config (shared loader), telemetry (shared setup), event bus subscribers, auth provider,
 * render. Startup failures are caught below and shown in plain DOM because React is not up yet.
 */
async function bootstrap(): Promise<void> {
  const config = await loadConfig();
  initAppInsights(config.APPINSIGHTS_CONNECTION_STRING);

  eventBus.subscribe("error", (event) => {
    trackError(event.error, { context: event.context ?? "unknown" });
    useUiStore.getState().addToast(event.error.message, "error");
  });

  const rootElement = document.getElementById("root");
  if (!rootElement) throw new Error("Root element #root not found in DOM");
  createRoot(rootElement).render(
    <StrictMode>
      <AuthProvider>
        <App />
      </AuthProvider>
    </StrictMode>,
  );
}

bootstrap().catch((error: unknown) => {
  trackError(error instanceof Error ? error : new Error(String(error)), { context: "bootstrap" });
  const rootElement = document.getElementById("root");
  if (!rootElement) return;
  const wrap = document.createElement("div");
  wrap.style.cssText = "display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:sans-serif;padding:1.5rem";
  const inner = document.createElement("div");
  inner.style.textAlign = "center";
  const heading = document.createElement("h1");
  heading.textContent = "Unable to load application";
  const detail = document.createElement("p");
  detail.textContent = error instanceof Error ? error.message : String(error);
  inner.append(heading, detail);
  wrap.append(inner);
  rootElement.replaceChildren(wrap);
});
